// src/sign_in_files/Step1.jsx
import React, { useState, useEffect } from 'react';
import { FIELD_LIMITS } from './DesignSystem';
import fundraiserApi from '../services/fundraiserApi';

const Step1 = ({
  onSuccess,
  onError,
  onLoading,
  onSubmitting,
  isLoading,
  isSubmitting,
  apiError,
  userId
}) => {
  const [formData, setFormData] = useState({
    title: '',
    goalAmount: '',
    purpose: 'medical',
    patientRelation: '',
    education: '',
    employmentStatus: '',
    heardAbout: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Validate userId on mount
  useEffect(() => {
    if (!userId) {
      console.error('❌ No userId provided to Step1');
    }
  }, [userId]);

  // Load existing data on mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        // Load from localStorage
        const userData = localStorage.getItem('completeUserData');
        if (userData) {
          const parsedData = JSON.parse(userData);
          if (parsedData.name && !formData.title) {
            setFormData(prev => ({
              ...prev,
              title: `Help ${parsedData.name} with medical treatment`
            }));
          }
        }

        // Check for existing draft
        if (userId) {
          const draftData = await fundraiserApi.checkUserDraft(userId);
          if (draftData && draftData.success && draftData.fundraiserId) {
            const fundraiserDetails = await fundraiserApi.getFundraiserById(draftData.fundraiserId);
            if (fundraiserDetails && fundraiserDetails.success) {
              const data = fundraiserDetails.data;
              setFormData(prev => ({
                ...prev,
                title: data.title || prev.title,
                goalAmount: data.goalAmount ? data.goalAmount.toString() : prev.goalAmount,
                purpose: data.purpose || prev.purpose,
                patientRelation: data.relation || data.patientRelation || prev.patientRelation,
                education: data.education || prev.education,
                employmentStatus: data.employment || data.employmentStatus || prev.employmentStatus,
                heardAbout: data.ref_source || data.heardAbout || prev.heardAbout,
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error loading existing data:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadExistingData();
  }, [userId]);

  const validateField = (field, value) => {
    let error = '';
    
    switch (field) {
      case 'title':
        if (!value.trim()) error = 'Title is required';
        else if (value.length < FIELD_LIMITS.title.min) error = `Title must be at least ${FIELD_LIMITS.title.min} characters`;
        break;
      case 'goalAmount':
        if (!value) error = 'Goal amount is required';
        else if (parseInt(value) < 2000) error = 'Minimum goal amount is ₹2000';
        else if (parseInt(value) > FIELD_LIMITS.goalAmount.max) error = `Maximum goal amount is ₹${FIELD_LIMITS.goalAmount.max.toLocaleString()}`;
        break;
      case 'patientRelation':
      case 'education':
      case 'employmentStatus':
      case 'heardAbout':
        if (!value || value.toString().trim() === '') error = 'This field is required';
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleBlur = (field) => {
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleInputChange = (field, value) => {
    if (field === 'goalAmount') {
      const numValue = parseInt(value.replace(/\D/g, '') || '0');
      if (numValue > FIELD_LIMITS.goalAmount.max) return;
      value = numValue === 0 ? '' : numValue.toString();
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newFieldErrors = {};
    const requiredFields = ['title', 'goalAmount', 'patientRelation', 'education', 'employmentStatus', 'heardAbout'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newFieldErrors[field] = error;
      }
    });

    setFieldErrors(newFieldErrors);
    return Object.keys(newFieldErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      onError('Please fill all required fields correctly.');
      return;
    }

    // Check userId
    if (!userId) {
      onError('User session not found. Please login again.');
      return;
    }

    try {
      onLoading(true);
      onError('');

      const payload = {
        userId: userId,
        title: formData.title,
        goalAmount: formData.goalAmount,
        purpose: formData.purpose || "medical",
        patientRelation: formData.patientRelation,
        education: formData.education,
        employmentStatus: formData.employmentStatus,
        heardAbout: formData.heardAbout,
      };

      console.log('📤 Saving fundraiser basic info for userId:', userId);
      
      const result = await fundraiserApi.saveFundraiserBasic(payload, userId);

      if (result.success) {
        console.log('✅ Step 1 saved successfully, fundraiserId:', result.fundraiserId || result.id);
        onSuccess(result.fundraiserId || result.id);
      } else {
        throw new Error(result.message || 'Failed to save basic details');
      }
    } catch (error) {
      console.error('❌ Form submission error:', error);
      
      let errorMessage = error.message || 'Something went wrong. Please try again.';
      if (typeof error === 'object' && error.error) {
        errorMessage = error.error;
      }

      onError(errorMessage);
    } finally {
      onLoading(false);
    }
  };

  const getSubmitButtonText = () => {
    if (isLoading) {
      return 'Saving...';
    }
    
    if (!userId) {
      return 'Session Expired';
    }
    
    return 'Save & Continue →';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
          Fundraiser Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          onBlur={() => handleBlur('title')}
          placeholder="Enter fundraiser title"
          className={`
            w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
            placeholder:text-gray-400 text-gray-900 text-sm
            ${fieldErrors.title 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}
          maxLength={FIELD_LIMITS.title.max}
          required
        />
        {fieldErrors.title && (
          <p className="mt-1 text-xs text-red-600 text-left">
            {fieldErrors.title}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
          Goal Amount (₹) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="goalAmount"
          value={formData.goalAmount}
          onChange={(e) => handleInputChange('goalAmount', e.target.value)}
          onBlur={() => handleBlur('goalAmount')}
          placeholder="Minimum ₹2000"
          className={`
            w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
            placeholder:text-gray-400 text-gray-900 text-sm
            ${fieldErrors.goalAmount 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}
          required
        />
        <div className="mt-1 text-xs text-gray-500 text-left">
          Minimum amount: ₹2,000 | Maximum amount: ₹{FIELD_LIMITS.goalAmount.max.toLocaleString()}
        </div>
        {fieldErrors.goalAmount && (
          <p className="mt-1 text-xs text-red-600 text-left">
            {fieldErrors.goalAmount}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Patient Relation <span className="text-red-500">*</span>
          </label>
          <select
            name="patientRelation"
            value={formData.patientRelation}
            onChange={(e) => handleInputChange('patientRelation', e.target.value)}
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              text-gray-900 text-sm bg-white
              ${fieldErrors.patientRelation 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            required
          >
            <option value="">Select relation</option>
            <option value="self">Self</option>
            <option value="spouse">Spouse</option>
            <option value="child">Child</option>
            <option value="parent">Parent</option>
            <option value="sibling">Sibling</option>
            <option value="friend">Friend</option>
            <option value="other">Other</option>
          </select>
          {fieldErrors.patientRelation && (
            <p className="mt-1 text-xs text-red-600 text-left">
              {fieldErrors.patientRelation}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
            Education <span className="text-red-500">*</span>
          </label>
          <select
            name="education"
            value={formData.education}
            onChange={(e) => handleInputChange('education', e.target.value)}
            className={`
              w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
              text-gray-900 text-sm bg-white
              ${fieldErrors.education 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            required
          >
            <option value="">Select education</option>
            <option value="high_school">High School</option>
            <option value="graduate">Graduate</option>
            <option value="post_graduate">Post Graduate</option>
            <option value="doctorate">Doctorate</option>
            <option value="other">Other</option>
          </select>
          {fieldErrors.education && (
            <p className="mt-1 text-xs text-red-600 text-left">
              {fieldErrors.education}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
          Employment Status <span className="text-red-500">*</span>
        </label>
        <select
          name="employmentStatus"
          value={formData.employmentStatus}
          onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
          className={`
            w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
            text-gray-900 text-sm bg-white
            ${fieldErrors.employmentStatus 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}
          required
        >
          <option value="">Select employment status</option>
          <option value="employed">Employed</option>
          <option value="self_employed">Self Employed</option>
          <option value="student">Student</option>
          <option value="unemployed">Unemployed</option>
          <option value="retired">Retired</option>
        </select>
        {fieldErrors.employmentStatus && (
          <p className="mt-1 text-xs text-red-600 text-left">
            {fieldErrors.employmentStatus}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
          How did you hear about us? <span className="text-red-500">*</span>
        </label>
        <select
          name="heardAbout"
          value={formData.heardAbout}
          onChange={(e) => handleInputChange('heardAbout', e.target.value)}
          className={`
            w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
            focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
            text-gray-900 text-sm bg-white
            ${fieldErrors.heardAbout 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
              : 'border-gray-200 hover:border-gray-300'
            }
          `}
          required
        >
          <option value="">Select option</option>
          <option value="friend">Friend/Family</option>
          <option value="social_media">Social Media</option>
          <option value="search_engine">Search Engine</option>
          <option value="news">News Article</option>
          <option value="other">Other</option>
        </select>
        {fieldErrors.heardAbout && (
          <p className="mt-1 text-xs text-red-600 text-left">
            {fieldErrors.heardAbout}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <div className={`flex justify-end mt-6 pt-5 border-t border-gray-100`}>
        <button
          type="submit"
          disabled={isLoading || isSubmitting || !userId}
          className={`
            px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
            ${isLoading || isSubmitting || !userId
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white hover:shadow-md hover:shadow-gsg-teal/25'
            }
            flex items-center justify-center
          `}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {getSubmitButtonText()}
            </>
          ) : (
            getSubmitButtonText()
          )}
        </button>
      </div>

      {/* Footer */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-blue-700 text-left">
            Your information is secure and will only be used for fundraising purposes.
          </p>
        </div>
      </div>
    </form>
  );
};

export default Step1;